import React, { useEffect, useState } from "react";
import { Sparkles, ArrowRight, Phone, Mail, Calendar as CalIcon, MapPin, MessageCircle, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import PrepareActionSheet from "./PrepareActionSheet";

/**
 * AIBriefCard — encart de synthèse IA affiché en haut de la fiche prospect.
 * Appelle automatiquement /api/ai/prospect-brief au mount, affiche
 * la synthèse en 3 lignes + un bouton "Préparer l'action" qui ouvre la sheet
 * PrepareActionSheet pré-routée sur le bon type d'action.
 *
 * Props:
 *   prospectId, prospect
 */
const ICONS = { call: Phone, email: Mail, rdv: CalIcon, visit: MapPin, linkedin: MessageCircle };
const ACTION_LABEL = {
  call: "Préparer l'appel",
  email: "Rédiger l'email",
  rdv: "Préparer le RDV",
  visit: "Brief de visite",
  linkedin: "Préparer le message",
};

export default function AIBriefCard({ prospectId, prospect }) {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [prepareOpen, setPrepareOpen] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    api.post("/ai/prospect-brief", { prospect_id: prospectId })
      .then((r) => setBrief(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Indisponible"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (prospectId) load(); /* eslint-disable-next-line */ }, [prospectId]);

  if (!prospectId) return null;

  const action = brief?.action;
  const ActionIcon = action?.type ? (ICONS[action.type] || Phone) : Sparkles;

  return (
    <>
      <div
        data-testid="ai-brief-card"
        className="relative rounded-2xl overflow-hidden border border-emerald-500/30"
        style={{
          background: "linear-gradient(135deg, rgba(0,220,130,0.18) 0%, rgba(56,189,248,0.05) 60%, rgba(0,0,0,0) 100%)",
          backdropFilter: "blur(10px)",
        }}
      >
        {/* Top label */}
        <div className="px-5 pt-4 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Sparkles className="w-3 h-3 text-emerald-300" strokeWidth={2.4} />
            <span className="text-[10px] font-bold uppercase tracking-[0.4em] text-emerald-300">Prosperr · Brief IA</span>
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="ai-brief-refresh"
            className="text-zinc-500 hover:text-emerald-300 transition-colors disabled:opacity-50"
            aria-label="Régénérer le brief"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        {/* Summary */}
        <div className="px-5 pt-2 pb-4 min-h-[60px]">
          {loading && (
            <div className="space-y-2 mt-1" aria-busy="true">
              <div className="h-3 w-11/12 rounded bg-white/8 animate-pulse" />
              <div className="h-3 w-9/12 rounded bg-white/8 animate-pulse" />
              <div className="h-3 w-7/12 rounded bg-white/8 animate-pulse" />
            </div>
          )}
          {error && (
            <p className="text-[13px] text-rose-300 mt-1">{error}</p>
          )}
          {!loading && brief && (
            <p className="text-[13.5px] text-white leading-relaxed" data-testid="ai-brief-summary">
              {brief.summary}
            </p>
          )}
        </div>

        {/* Signals (chips) */}
        {!loading && Array.isArray(brief?.signals) && brief.signals.length > 0 && (
          <div className="px-5 pb-3 flex flex-wrap gap-1.5" data-testid="ai-brief-signals">
            {brief.signals.slice(0, 3).map((sig, i) => (
              <span
                key={i}
                className="text-[10.5px] font-medium text-zinc-300 px-2 py-0.5 rounded-md bg-white/5 border border-white/10"
              >
                {sig}
              </span>
            ))}
          </div>
        )}

        {/* Recommended action — CTA */}
        {!loading && action && (
          <button
            onClick={() => setPrepareOpen(true)}
            data-testid="ai-brief-action-cta"
            className="w-full flex items-center gap-3 px-5 py-3.5 border-t border-emerald-500/30 bg-emerald-500 hover:bg-emerald-500 active:scale-[0.998] transition-colors text-left text-white"
          >
            <div className="w-9 h-9 rounded-lg bg-white/15 border border-white/25 grid place-items-center shrink-0">
              <ActionIcon className="w-4 h-4 text-white" strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13.5px] font-semibold leading-tight">{action.label || ACTION_LABEL[action.type] || "Préparer l'action"}</div>
              {action.reason && (
                <div className="text-[11.5px] text-emerald-100/80 mt-0.5 leading-snug truncate">{action.reason}</div>
              )}
            </div>
            <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-100/90 shrink-0">
              ✨ <span>Préparer</span> <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </button>
        )}
      </div>

      <PrepareActionSheet
        open={prepareOpen}
        onOpenChange={setPrepareOpen}
        prospectId={prospectId}
        prospect={prospect}
        actionType={action?.type || "auto"}
      />
    </>
  );
}
